/**
 * Browser-side application logger for the Cumpli SPA.
 *
 * Why: so that "what's going on when using the website" is legible in the
 * DevTools console — every API call, auth transition, and route change shows up
 * as a compact, colour-coded, namespaced line — and so automated checks that
 * read the console (Playwright, Claude-in-Chrome) get a structured signal
 * instead of silence.
 *
 * Verbosity is runtime-adjustable without a rebuild:
 *   - build default: `VITE_LOG_LEVEL` (falls back to 'info' in dev, 'warn' in prod)
 *   - live override:  `localStorage['cumpli:logLevel'] = 'debug'` then reload,
 *                      or call `window.cumpliLog.setLevel('debug')`.
 *
 * Safety: values are deeply sanitized (recursively, bounded in depth) so auth
 * tokens / secrets / PII never land in the console even if a caller passes a
 * whole object with nested sensitive fields.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

const WEIGHT: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  silent: 100,
};

const LEVELS: LogLevel[] = ['debug', 'info', 'warn', 'error', 'silent'];

const STORAGE_KEY = 'cumpli:logLevel';

function isLevel(v: string | null | undefined): v is LogLevel {
  return !!v && (LEVELS as string[]).includes(v);
}

function defaultLevel(): LogLevel {
  const fromEnv = import.meta.env.VITE_LOG_LEVEL;
  if (isLevel(fromEnv)) return fromEnv;
  return import.meta.env.DEV ? 'info' : 'warn';
}

let currentLevel: LogLevel = (() => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLevel(stored)) return stored;
  } catch {
    // localStorage can throw in private mode / sandboxed frames — ignore.
  }
  return defaultLevel();
})();

// Keys whose values are always redacted, matched case-insensitively as a
// substring. Aligned with the API logger's PII key list (packages/api) so the
// same sensitive fields stay out of the browser console — including nested PII
// in a compliance product. `requestId` is intentionally NOT here: the
// x-request-id correlation id is safe and must remain visible.
const REDACT =
  /(token|authorization|password|secret|apikey|api_key|credential|cookie|ssn|email|phone|fullname|full_name|nombre|cedula|idnumber|id_number|docid|doc_id|address)/i;

const MAX_DEPTH = 4;

/**
 * Deep-copy `value` while redacting sensitive keys and serializing Errors.
 * Mirrors the API logger's sanitize so browser and server logs redact the same
 * fields. Bounded in depth so a cyclic or huge object can never wedge the logger.
 */
function sanitize(value: unknown, depth = 0): unknown {
  if (value == null) return value;
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') return value;
  if (t === 'bigint') return (value as bigint).toString();
  if (depth >= MAX_DEPTH) return '[truncated]';
  if (Array.isArray(value)) {
    return value.slice(0, 50).map((v) => sanitize(v, depth + 1));
  }
  if (t === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = REDACT.test(k) ? '[redacted]' : sanitize(v, depth + 1);
    }
    return out;
  }
  // functions, symbols, etc.
  return String(value);
}

// Muted namespace tag colours — readable on both light and dark DevTools themes.
const SCOPE_STYLE = 'color:#6366f1;font-weight:600';
const LEVEL_STYLE: Record<Exclude<LogLevel, 'silent'>, string> = {
  debug: 'color:#94a3b8',
  info: 'color:#0ea5e9',
  warn: 'color:#d97706',
  error: 'color:#dc2626;font-weight:600',
};

export interface Logger {
  debug(msg: string, fields?: Record<string, unknown>): void;
  info(msg: string, fields?: Record<string, unknown>): void;
  warn(msg: string, fields?: Record<string, unknown>): void;
  error(msg: string, fields?: Record<string, unknown>): void;
  child(scope: string): Logger;
}

function emit(
  level: Exclude<LogLevel, 'silent'>,
  scope: string,
  msg: string,
  fields?: Record<string, unknown>
): void {
  if (WEIGHT[level] < WEIGHT[currentLevel]) return;
  const time = new Date().toISOString().slice(11, 23);
  // `%c` spans colour the scope tag and the message independently in DevTools.
  const line = `${time} %c${scope}%c ${msg}`;
  const styles = [SCOPE_STYLE, LEVEL_STYLE[level]];
  const method =
    level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
  const payload =
    fields && Object.keys(fields).length
      ? (sanitize(fields) as Record<string, unknown>)
      : undefined;
  if (payload) method(line, ...styles, payload);
  else method(line, ...styles);
}

function makeLogger(scope: string): Logger {
  return {
    debug: (msg, fields) => emit('debug', scope, msg, fields),
    info: (msg, fields) => emit('info', scope, msg, fields),
    warn: (msg, fields) => emit('warn', scope, msg, fields),
    error: (msg, fields) => emit('error', scope, msg, fields),
    child: (child) => makeLogger(`${scope}:${child}`),
  };
}

/** Root logger. Use `.child('api')` etc. for a namespaced sub-logger. */
export const log = makeLogger('cumpli');

/** Change verbosity at runtime; persists across reloads via localStorage. */
export function setLogLevel(level: LogLevel): void {
  currentLevel = level;
  try {
    localStorage.setItem(STORAGE_KEY, level);
  } catch {
    // ignore storage failures
  }
  // Use console directly so this confirmation shows even at 'silent'.
  console.info(`[cumpli] log level set to '${level}'`);
}

export function getLogLevel(): LogLevel {
  return currentLevel;
}

// Expose a tiny console handle so a tester can bump verbosity live:
//   cumpliLog.setLevel('debug')
if (typeof window !== 'undefined') {
  (window as unknown as { cumpliLog: unknown }).cumpliLog = {
    setLevel: setLogLevel,
    getLevel: getLogLevel,
    levels: LEVELS,
  };
}

/** Exposed for unit tests. */
export const __test = { sanitize };
